import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Box, Select, TextButton } from '@zillow/constellation-10';
import { format } from 'date-fns';
import { GraphWrapper } from '../../../../../SharedComponents/GraphWrapper';
import { CustomGraphTooltip } from '../../../../../SharedComponents/CustomGraphTooltip';
import { CustomGraphLegend } from '../../../../../SharedComponents/CustomGraphLegend';
import { formatCurrency } from '../../../../../../utils/formatCurrency';
import { getGraphTitle } from '../../../../../../utils/getGraphTitle';
import { useMainLayout } from '../../../../../layouts/MainPageLayout/MainLayoutContext';
import { formatLocalDate } from '../../../../../../utils/localDateToDate';
import { bedroomSelectOptions, CompsMonthTick, MARKET_AVG_COLOR } from './RentComps.utils';
import type { CostPerSqFtChartDataPoint, PropertyOption, RentCompsAverages } from './RentComps.types';

type CostPerSqFtCompsGraphProps = {
  chartData: Array<CostPerSqFtChartDataPoint>;
  averages: RentCompsAverages;
  isLoading: boolean;
  selectedPropertyName: string;
  allCompNames: Array<string>;
  allCompsColorMapping: Record<string, string>;
  numBedrooms: number;
  setNumBedrooms: (value: number) => void;
  propertyOptions: Array<PropertyOption>;
  selectedUnitId: number | null;
  setSelectedUnitId: (value: number | null) => void;
  onEditCompetitors: () => void;
};

export const CostPerSqFtCompsGraph = ({
  chartData,
  averages,
  isLoading,
  selectedPropertyName,
  allCompNames,
  allCompsColorMapping,
  numBedrooms,
  setNumBedrooms,
  propertyOptions,
  selectedUnitId,
  setSelectedUnitId,
  onEditCompetitors,
}: CostPerSqFtCompsGraphProps) => {
  const {
    filteredProperties,
    regionsMapping,
    selectedRegionId,
    selectedFilters,
    isFetchingInitialData,
    analyticsLastUpdatedDates,
  } = useMainLayout();

  const lastUpdatedPortfolioDate = formatLocalDate(analyticsLastUpdatedDates?.portfolioRentLastUpdated);
  const lastUpdatedMarketDate = formatLocalDate(analyticsLastUpdatedDates?.marketRentLastUpdated);
  const currentDateFormatted = format(new Date(), 'MMM yyyy');
  const bedsLabel = bedroomSelectOptions.find((o) => o.value === numBedrooms)?.label ?? 'All Beds';

  const title = getGraphTitle({
    graphName: 'Cost per square foot comps',
    regionsMapping,
    selectedRegionId,
    selectedFilters,
    filteredProperties,
  });

  const allSeriesNames = selectedPropertyName ? [selectedPropertyName, ...allCompNames] : allCompNames;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const labelTypes: Record<string, any> = {};
  allSeriesNames.forEach((name) => {
    labelTypes[name] = 'line';
  });
  labelTypes['Month to date'] = 'dashed-line';
  labelTypes[`Market average, ${bedsLabel}`] = 'default';

  return (
    <GraphWrapper
      graphId="cost-per-sqft-comps"
      title={title}
      subtitle={`How your average cost per square foot compares to your competitors and the market average.${lastUpdatedPortfolioDate && lastUpdatedMarketDate ? ` Portfolio data updated ${lastUpdatedPortfolioDate}, market data updated ${lastUpdatedMarketDate}.` : ''}`}
      loading={isLoading || isFetchingInitialData}
      infoCards={[
        {
          title: 'Portfolio average',
          infoText: 'Average cost per square foot for your properties in the selected market, month to date.',
          value: formatCurrency(averages.portfolioAverage),
        },
        {
          title: 'Competitor average',
          infoText: 'Average cost per square foot for competitor properties in the selected market, month to date.',
          value: formatCurrency(averages.competitorAverage),
        },
      ]}
      headerAction={
        <TextButton onClick={onEditCompetitors} css={{ color: 'text.link' }}>
          Edit competitors
        </TextButton>
      }
      customFilters={
        <Box css={{ display: 'flex', gap: '200' }}>
          <Select
            size="md"
            value={String(selectedUnitId ?? '')}
            onChange={(e) => setSelectedUnitId(Number(e.target.value))}
            css={{ maxWidth: '250px' }}
          >
            {propertyOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Select
            size="md"
            value={numBedrooms}
            onChange={(e) => setNumBedrooms(Number(e.target.value))}
            css={{ maxWidth: '150px' }}
          >
            {bedroomSelectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Box>
      }
    >
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <YAxis tickFormatter={(value) => `$${Number(value).toFixed(2)}`} />
          <XAxis dataKey="name" interval={0} tick={<CompsMonthTick />} />
          <Tooltip
            formatter={(value) => formatCurrency(Number(value))}
            content={
              <CustomGraphTooltip
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (filteredPayload: Array<any>, displayLabel: string) => {
                    const isCurrentMonth = displayLabel === currentDateFormatted;
                    if (isCurrentMonth) {
                      return filteredPayload.filter(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (entry: any) => entry.dataKey?.endsWith(' month to date'),
                      );
                    }
                    return filteredPayload.filter(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (entry: any) => !entry.dataKey?.endsWith(' month to date'),
                    );
                  }
                }
              />
            }
          />
          <Legend
            wrapperStyle={{ marginBottom: '-32px' }}
            content={
              <CustomGraphLegend
                labelTypes={labelTypes}
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (payload: Array<any>) => {
                    const filtered = payload.filter(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (entry: any) => !entry.value?.endsWith(' month to date'),
                    );
                    const order = [...allSeriesNames, 'Month to date', `Market average, ${bedsLabel}`];
                    return filtered.sort(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (a: any, b: any) => order.indexOf(a.value) - order.indexOf(b.value),
                    );
                  }
                }
              />
            }
          />
          <Bar
            dataKey="Market average"
            fill={MARKET_AVG_COLOR}
            name={`Market average, ${bedsLabel}`}
            stackId="marketAvg"
            barSize={40}
          />
          <Bar
            dataKey="Market average month to date"
            fill={MARKET_AVG_COLOR}
            legendType="none"
            stackId="marketAvg"
            barSize={40}
          />
          {allSeriesNames.map((name) => (
            <Line
              key={name}
              type="linear"
              dataKey={name}
              stroke={allCompsColorMapping[name] || '#000'}
              strokeWidth={3}
            />
          ))}
          {allSeriesNames.map((name) => (
            <Line
              key={`${name}-mtd`}
              type="linear"
              dataKey={`${name} month to date`}
              stroke={allCompsColorMapping[name] || '#000'}
              strokeWidth={3}
              strokeDasharray="5 5"
              dot={false}
              legendType="none"
            />
          ))}
          <Line type="linear" dataKey="Month to date" stroke="#666666" strokeDasharray="5 5" dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </GraphWrapper>
  );
};
